import { getCurrencyOptions, getCurrencySymbol, getLocaleNumberFormats } from './Amalgam';

const MAX_DIGITS = 22;
const DECIMAL_SEP = '.';
const ZERO_CHAR = '0';

const parse = numStr => {
  let exponent = 0;
  let digits;
  let numberOfIntegerDigits;
  let i;
  let j;
  let zeros;

  // Decimal point?
  if ((numberOfIntegerDigits = numStr.indexOf(DECIMAL_SEP)) > -1) {
    numStr = numStr.replace(DECIMAL_SEP, '');
  }

  // Exponential form?
  if ((i = numStr.search(/e/i)) > 0) {
    // Work out the exponent.
    if (numberOfIntegerDigits < 0) numberOfIntegerDigits = i;
    numberOfIntegerDigits += +numStr.slice(i + 1);
    numStr = numStr.substring(0, i);
  } else if (numberOfIntegerDigits < 0) {
    // There was no decimal point or exponent so it is an integer.
    numberOfIntegerDigits = numStr.length;
  }

  // Count the number of leading zeros.
  for (i = 0; numStr.charAt(i) == ZERO_CHAR; i += 1) {
    /* jshint noempty: false */
  }

  if (i == (zeros = numStr.length)) {
    // The digits are all zero.
    digits = [0];
    numberOfIntegerDigits = 1;
  } else {
    // Count the number of trailing zeros
    zeros--;
    while (numStr.charAt(zeros) == ZERO_CHAR) zeros--;

    // Trailing zeros are insignificant so ignore them
    numberOfIntegerDigits -= i;
    digits = [];
    // Convert string to array of digits without leading/trailing zeros.
    for (j = 0; i <= zeros; i++, j++) {
      digits[j] = +numStr.charAt(i);
    }
  }

  // If the number overflows the maximum allowed digits then use an exponent.
  if (numberOfIntegerDigits > MAX_DIGITS) {
    digits = digits.splice(0, MAX_DIGITS - 1);
    exponent = numberOfIntegerDigits - 1;
    numberOfIntegerDigits = 1;
  }

  return { d: digits, e: exponent, i: numberOfIntegerDigits };
};

const roundNumber = (parsedNumber, fractionSize, minFrac, maxFrac) => {
  const digits = parsedNumber.d;
  let fractionLen = digits.length - parsedNumber.i;

  // determine fractionSize if it is not specified; `+fractionSize` converts it to a number
  fractionSize =
    fractionSize === undefined ? Math.min(Math.max(minFrac, fractionLen), maxFrac) : +fractionSize;

  // The index of the digit to where rounding is to occur
  let roundAt = fractionSize + parsedNumber.i;
  const digit = digits[roundAt];

  if (roundAt > 0) {
    // Drop fractional digits beyond `roundAt`
    digits.splice(Math.max(parsedNumber.i, roundAt));

    // Set non-fractional digits beyond `roundAt` to 0
    for (let j = roundAt; j < digits.length; j++) {
      digits[j] = 0;
    }
  } else {
    // We rounded to zero so reset the parsedNumber
    fractionLen = Math.max(0, fractionLen);
    parsedNumber.i = 1;
    digits.length = Math.max(1, (roundAt = fractionSize + 1));
    digits[0] = 0;
    for (let i = 1; i < roundAt; i++) digits[i] = 0;
  }

  if (digit >= 5) {
    if (roundAt - 1 < 0) {
      for (let k = 0; k > roundAt; k--) {
        digits.unshift(0);
        parsedNumber.i++;
      }
      digits.unshift(1);
      parsedNumber.i++;
    } else {
      digits[roundAt - 1]++;
    }
  }

  // Pad out with zeros to get the required fraction length
  for (; fractionLen < Math.max(0, fractionSize); fractionLen++) digits.push(0);

  // Do any carrying, e.g. a digit was rounded up to 10
  const carry = digits.reduceRight((carry, d, i, digits) => {
    d = d + carry;
    digits[i] = d % 10;
    return Math.floor(d / 10);
  }, 0);
  if (carry) {
    digits.unshift(carry);
    parsedNumber.i++;
  }
};

const formatNumber = (number, pattern, groupSep, decimalSep, fractionSize) => {
  if (typeof number !== 'number') {
    return '';
  }

  const isInfinity = !isFinite(number);
  let isZero = false;
  const numStr = `${Math.abs(number)}`;
  let formattedText = '';
  let parsedNumber;

  if (isInfinity) {
    formattedText = '\u221e';
  } else {
    parsedNumber = parse(numStr);

    roundNumber(parsedNumber, fractionSize, pattern.minFrac, pattern.maxFrac);

    let digits = parsedNumber.d;
    let integerLen = parsedNumber.i;
    const exponent = parsedNumber.e;
    let decimals = [];
    isZero = digits.reduce((isZero, d) => isZero && !d, true);

    // pad zeros for small numbers
    while (integerLen < 0) {
      digits.unshift(0);
      integerLen++;
    }

    // extract decimals digits
    if (integerLen > 0) {
      decimals = digits.splice(integerLen, digits.length);
    } else {
      decimals = digits;
      digits = [0];
    }

    // format the integer digits with grouping separators
    const groups = [];
    if (digits.length >= pattern.lgSize) {
      groups.unshift(digits.splice(-pattern.lgSize, digits.length).join(''));
    }
    while (digits.length > pattern.gSize) {
      groups.unshift(digits.splice(-pattern.gSize, digits.length).join(''));
    }
    if (digits.length) {
      groups.unshift(digits.join(''));
    }
    formattedText = groups.join(groupSep);

    // append the decimal digits
    if (decimals.length) {
      formattedText += decimalSep + decimals.join('');
    }

    if (exponent) {
      formattedText += `e+${exponent}`;
    }
  }

  if (number < 0 && !isZero) {
    return pattern.negPre + formattedText + pattern.negSuf;
  }

  return pattern.posPre + formattedText + pattern.posSuf;
};

const computeFormattedPrice = amount => {
  const formats = getLocaleNumberFormats();
  const fractionSize = formats.PATTERNS[1].maxFrac;

  const symbol = getCurrencySymbol();
  const currencyOptions = getCurrencyOptions();

  let formatted =
    typeof amount !== 'number'
      ? amount
      : formatNumber(
        amount,
        formats.PATTERNS[1],
        formats.GROUP_SEP,
        formats.DECIMAL_SEP,
        fractionSize
      ).replace(/[\u00A4]/g, symbol);

  if (!formatted) {
    return formatted;
  }

  const symbolLength = symbol.length;
  const symbolPos = formatted.indexOf(symbol);

  const isSymbolAtEnd = symbolPos + symbolLength === formatted.length;

  if (isSymbolAtEnd) {
    formatted = formatted.slice(0, -1 * symbolLength);
  }

  const parts = formatted.split(formats.DECIMAL_SEP || '.');

  if (parseInt(parts[parts.length - 1], 10) === 0) {
    formatted = parts.slice(0, parts.length - 1).join(formats.DECIMAL_SEP || '.');
  }

  if (isSymbolAtEnd) {
    formatted += symbol;
  }

  if (currencyOptions && currencyOptions.symbolOnRight && !isSymbolAtEnd) {
    formatted = `${formatted.slice(symbolLength)} ${symbol}`;
  }

  return formatted;
};

export const getFormattedPrice = price => {
  if (typeof price !== 'number') {
    return '';
  }

  try {
    const formattedPrice = computeFormattedPrice(price);

    return formattedPrice || '';
  } catch (ex) {
    return '';
  }
};
